import type { ModuleContext } from "@vortex-os/core";
import type { CommandRegistry } from "./registry.js";
import type { CommandArg, CommandInput } from "./types.js";

export interface RunOptions {
  readonly registry: CommandRegistry;
  readonly context: ModuleContext;
}

/**
 * Thrown by `runSlash` when the requested command name does not exist
 * in the registry. The unknown name is exposed for caller diagnostics.
 */
export class CommandNotFoundError extends Error {
  readonly commandName: string;

  constructor(commandName: string) {
    super(`Unknown command: ${commandName}`);
    this.name = "CommandNotFoundError";
    this.commandName = commandName;
  }
}

/**
 * Parse an input string and dispatch the matching command.
 *
 * Accepted forms (leading slash optional):
 *   "name"
 *   "/name"
 *   "name arg1 arg2 ..."
 *
 * Returns whatever the command's handler returns (awaited if it is async).
 */
export async function runSlash(
  input: string,
  { registry, context }: RunOptions,
): Promise<unknown> {
  const trimmed = input.trim().replace(/^\//, "");
  if (trimmed.length === 0) {
    throw new Error("Empty command input");
  }

  const tokens = trimmed.split(/\s+/);
  const name = tokens[0] ?? "";
  const tail = tokens.slice(1);

  const command = registry.get(name);
  if (!command) {
    throw new CommandNotFoundError(name);
  }

  const args = parseArgs(tail, command.args);
  const rest = tail.join(" ");
  const ci: CommandInput = {
    raw: trimmed,
    args,
    rest,
    context,
  };
  return command.handler(ci);
}

/**
 * Dispatch a command by name with pre-split argument tokens, skipping the
 * whitespace split `runSlash` does on a raw string. Use this when the caller
 * already holds clean tokens (e.g. the shell-split `vortex` CLI argv): the
 * tokens reach the handler verbatim via `CommandInput.argv`, so quotes, spaces,
 * or empty values inside a token can never shift the boundaries of later tokens
 * (the lossy hazard of joining argv into a string and re-tokenizing it).
 *
 * `argv` is the tokens AFTER the command name. `rest`/`args` are still derived
 * for handlers that read them, but a quote-aware command should prefer `argv`.
 */
export async function runSlashArgv(
  name: string,
  argv: readonly string[],
  { registry, context }: RunOptions,
): Promise<unknown> {
  if (name.length === 0) {
    throw new Error("Empty command name");
  }
  const command = registry.get(name);
  if (!command) {
    throw new CommandNotFoundError(name);
  }
  const args = parseArgs(argv, command.args);
  const ci: CommandInput = {
    raw: [name, ...argv].join(" "),
    args,
    rest: argv.join(" "),
    argv,
    context,
  };
  return command.handler(ci);
}

function parseArgs(
  tokens: readonly string[],
  schema: readonly CommandArg[] | undefined,
): Record<string, string> {
  const out: Record<string, string> = {};
  if (!schema) return out;
  for (let i = 0; i < schema.length; i += 1) {
    const arg = schema[i];
    const value = tokens[i];
    if (arg && value !== undefined) {
      out[arg.name] = value;
    }
  }
  return out;
}
